import React, { useEffect, useRef, useState } from 'react'
import styles from '~/styles/molecules/navigation/navigation.module.scss'
import { useTranslation } from 'react-i18next'
import { useSortableItem } from '~/components/utils/sort/sotableItem'

import { addClass } from '~/common/utils/html'
import { GeneralIconTag, IconTag } from '~/common/define/icon'
import { SingleMenuMode } from '~/common/define/menu'
import { getWidth, isEllipsisActive } from '~/common/utils/rect'
import { ColorWeight, lightenColor, SpaceColor } from '~/common/define/color'
import Icon, { IconColor, IconFrame, IconSize } from '~/components/atoms/icon/Icon'
import SwitchLink from '~/components/atoms/link/SwitchLink'
import IconButton from '~/components/atoms/icon/IconButton'
import TooltipWrapper from '~/components/wrapper/tooltip/TooltipWrapper'

export interface NavigationMenuItemVar {
  id: number
  text: string
  iconTag: IconTag
  link: string
  onEdit?(id: number): void
  external?: boolean
  lock?: boolean
  onPin(id: number): void
  pin: boolean
  accessible: boolean
  active: boolean
  shown: boolean
}

interface Props {
  item: NavigationMenuItemVar
  mode: SingleMenuMode
  color: SpaceColor
}

const NavigationMenuItem = ({item, mode, color}: Props) => {
  const {t} = useTranslation()
  const ref = useRef()
  const textRef = useRef()
  const [editLeft, setEditLeft] = useState<number>(0)
  const sortableItemProps = useSortableItem(item.id, mode != SingleMenuMode.SORT)
  const disable = mode == SingleMenuMode.DISABLE || (SingleMenuMode.VIEW && !item.accessible)

  useEffect(() => {
    const width = getWidth(ref.current)
    if (width > 0) {
      setEditLeft(width)
    }
  }, [ref, item, mode])

  return (
    <li className={styles.menu__item + addClass(styles.menu__itemClose, !item.shown)} {...sortableItemProps}>
      <style jsx>{`
        li:hover {
          background-color: ${(disable || item.active) ? '' : lightenColor(color, ColorWeight.LIGHT)};
        }
        li:active {
          background-color: ${(disable || item.active) ? '' : lightenColor(color, ColorWeight.WELTER)};
        }
        li {
          background-color: ${item.active ? lightenColor(color, ColorWeight.LIGHT) : 'transparent'}
        }
     `}</style>
      <SwitchLink link={(mode == SingleMenuMode.VIEW && item.accessible && !item.active) ? item.link : null}
                  external={item.external}
                  className={styles.menu__item__link}>
        <TooltipWrapper text={item.text} off={!isEllipsisActive(textRef)} className={styles.menu__item__main} wrapRef={ref}>
          {mode == SingleMenuMode.SORT ?
            <Icon tag={GeneralIconTag.DRAGGABLE} size={IconSize.XX_SMALL} frame={IconFrame.CIRCLE}
                  fillColor={IconColor.DARK_GRAY}
                  bgColor={lightenColor(color, ColorWeight.WELTER)}
            /> :
            <Icon tag={item.iconTag} size={IconSize.XX_SMALL}/>
          }
          <span className={styles.menu__item__text + addClass(styles.menu__item__text__disable, disable)} ref={textRef}>
            {item.text}
          </span>
          {item.external &&
            <Icon tag={GeneralIconTag.EXTERNAL} size={IconSize.XX_SMALL}/>
          }
          {item.lock &&
            <Icon tag={GeneralIconTag.LOCK} size={IconSize.XX_SMALL} fillColor={IconColor.DARK_GRAY}/>
          }
        </TooltipWrapper>
      </SwitchLink>
      <span
        className={styles.menu__item__pin
          + addClass(styles.menu__item__pinActive, !disable)
          + addClass(styles.menu__item__pinShow, item.pin)}
      >
        <IconButton tag={GeneralIconTag.PIN} size={IconSize.XX_SMALL} fillColor={item.pin ? disable ? IconColor.GRAY : color : lightenColor(color, ColorWeight.WELTER)}
                    frame={(disable || mode == SingleMenuMode.SORT) ? null : IconFrame.CIRCLE} frameColor={IconColor.GRAY} hoveredShowFrame
                    onClick={() => !disable && item.onPin(item.id)} toolTipOff={disable || mode == SingleMenuMode.SORT}
                    toolTipText={t('navigation:tooltip.priority_shown')}/>
      </span>
      {item.onEdit && !disable &&
        <div className={styles.menu__item__edit}
              style={{left: editLeft}}
        >
          <IconButton
            onClick={() => item.onEdit(item.id)} toolTipText={t('navigation:tooltip.edit')}
            tag={GeneralIconTag.EDIT} size={IconSize.XX_SMALL}
            fillColor={IconColor.DARK_GRAY} frame={IconFrame.CIRCLE} frameColor={IconColor.DARK_GRAY}
            hoveredShowFrame
          />
        </div>
      }
    </li>
  )
}

export default NavigationMenuItem